import React, { useEffect, useRef, useState } from 'react';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AUCTIONS_SEARCH_PARAMS,
  createSearchPageTo,
  isMainSearchTypeKeywords,
  isOriginInUse,
} from '../../../util/search';
import {
  createSlug,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  parse,
  stringify,
} from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  AvatarLarge,
  AvatarSmall,
  Button,
  ExternalLink,
  InlineTextButton,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
  NamedLink,
  NotificationBadge,
  PrimaryButton,
} from '../../../components';
import { getSearchPageResourceLocatorStringParams } from '../../SearchPage/SearchPage.shared';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import { isTopbarMenuPageActive } from './currentPageMatch';

import css from './Topbar.module.css';
import { getCurrentUserTypeRoles, showCreateListingLinkForUser } from '../../../util/userHelpers';
import CustomLinksMenu from './TopbarDesktop/CustomLinksMenu/CustomLinksMenu';

import { ensureCurrentUser } from '../../../util/data';
import {
  checkAdminVerificationAccess,
  resetAdminVerificationAccessCache,
  syncMarketplaceNotifications,
} from '../../../util/api';
import {
  DEFAULT_LISTING_CREATION_PATH,
  getApprovedSellerVerificationRedirectTarget,
  getCreateListingRedirectTarget,
  getSellerApprovalMatrix,
  getSellerVerificationNotificationEventId,
  isSellerApproved,
  shouldShowSellerVerificationNavigation,
} from '../../../util/sellerVerification';
import {
  getLatestOfferNotification,
  getLatestOfferNotificationEventId,
} from '../../../util/offerNotifications';
import {
  getLatestMarketplaceNotification,
  getLatestMarketplaceNotificationEventId,
} from '../../../util/marketplaceNotifications';
import Icons from '../../../components/Icons/Icons';
import {
  FiChevronRight,
  FiCreditCard,
  FiGrid,
  FiHeart,
  FiInfo,
  FiLink,
  FiMessageCircle,
  FiPlusCircle,
  FiSettings,
  FiShoppingBag,
  FiShoppingCart,
  FiUser,
} from 'react-icons/fi';
import { FiExternalLink } from 'react-icons/fi';
import { MdGavel } from 'react-icons/md';
const MAX_MOBILE_SCREEN_WIDTH = 768;

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';
const SELLER_VERIFICATION_BANNER_STORAGE_KEY = 'sellerVerificationBannerSeenEventId';
const OFFER_NOTIFICATION_BANNER_STORAGE_KEY = 'offerNotificationBannerSeenEventId';
const MARKETPLACE_NOTIFICATION_BANNER_STORAGE_KEY = 'marketplaceNotificationBannerSeenEventId';
const MARKETPLACE_NOTIFICATION_SYNC_INTERVAL = 60 * 1000;

const readSellerVerificationBannerEventId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return window.localStorage.getItem(SELLER_VERIFICATION_BANNER_STORAGE_KEY) || '';
  } catch (_error) {
    return '';
  }
};

const writeSellerVerificationBannerEventId = eventId => {
  if (typeof window === 'undefined' || !eventId) {
    return;
  }

  try {
    window.localStorage.setItem(SELLER_VERIFICATION_BANNER_STORAGE_KEY, eventId);
  } catch (_error) {}
};

const readOfferNotificationBannerEventId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return window.localStorage.getItem(OFFER_NOTIFICATION_BANNER_STORAGE_KEY) || '';
  } catch (_error) {
    return '';
  }
};

const writeOfferNotificationBannerEventId = eventId => {
  if (typeof window === 'undefined' || !eventId) {
    return;
  }

  try {
    window.localStorage.setItem(OFFER_NOTIFICATION_BANNER_STORAGE_KEY, eventId);
  } catch (_error) {}
};

const readMarketplaceNotificationBannerEventId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return window.localStorage.getItem(MARKETPLACE_NOTIFICATION_BANNER_STORAGE_KEY) || '';
  } catch (_error) {
    return '';
  }
};

const writeMarketplaceNotificationBannerEventId = eventId => {
  if (typeof window === 'undefined' || !eventId) {
    return;
  }

  try {
    window.localStorage.setItem(MARKETPLACE_NOTIFICATION_BANNER_STORAGE_KEY, eventId);
  } catch (_error) {}
};

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isTransactionsPage = found => {
  const routeName = found.route?.name;
  if (routeName === 'OrdersPage' || routeName === 'OrderDetailsPage') {
    return 'TransactionsPage:orders';
  }
  if (routeName === 'SalesPage' || routeName === 'SaleDetailsPage') {
    return 'TransactionsPage:sales';
  }
  if (routeName === 'InboxPage') {
    const tab = found.params?.tab === 'selling' ? 'sales' : 'orders';
    return `TransactionsPage:${tab}`;
  }
  return null;
};
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const transactionsPageName = isTransactionsPage(found);
    return cmsPageName
      ? cmsPageName
      : transactionsPageName
      ? transactionsPageName
      : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};
const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onFetchCurrentUser,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
    viewport,
  } = props;
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasAdminVerificationAccess, setHasAdminVerificationAccess] = useState(false);
  const [dismissedSellerVerificationEventId, setDismissedSellerVerificationEventId] = useState('');
  const [dismissedOfferNotificationEventId, setDismissedOfferNotificationEventId] = useState('');
  const [
    dismissedMarketplaceNotificationEventId,
    setDismissedMarketplaceNotificationEventId,
  ] = useState('');
  const previousUserIdRef = useRef(null);
  useEffect(() => {
    setMounted(true);
  }, []);

  const currentUserId = currentUser?.id?.uuid || null;

  useEffect(() => {
    let isActive = true;

    if (!isAuthenticated || !currentUserId) {
      previousUserIdRef.current = null;
      resetAdminVerificationAccessCache();
      setHasAdminVerificationAccess(false);
      return () => {
        isActive = false;
      };
    }

    if (previousUserIdRef.current !== currentUserId) {
      resetAdminVerificationAccessCache();
      previousUserIdRef.current = currentUserId;
    }

    checkAdminVerificationAccess().then(allowed => {
      if (isActive) {
        setHasAdminVerificationAccess(allowed);
      }
    });

    return () => {
      isActive = false;
    };
  }, [isAuthenticated, currentUserId]);

  useEffect(() => {
    if (!mounted || typeof window === 'undefined') {
      return;
    }

    setDismissedSellerVerificationEventId(readSellerVerificationBannerEventId());
    setDismissedOfferNotificationEventId(readOfferNotificationBannerEventId());
    setDismissedMarketplaceNotificationEventId(readMarketplaceNotificationBannerEventId());
  }, [mounted, currentUserId]);

  const latestMarketplaceNotification = getLatestMarketplaceNotification(currentUser);
  const latestMarketplaceNotificationEventId = getLatestMarketplaceNotificationEventId(currentUser);

  useEffect(() => {
    if (!mounted || !isAuthenticated || !currentUserId) {
      return undefined;
    }

    let cancelled = false;

    const runNotificationSync = () => {
      syncMarketplaceNotifications({ source: 'topbar' })
        .then(response => {
          const payload = response?.data || {};
          const latestEventId = payload?.latestEventId || null;
          const shouldRefreshCurrentUser =
            (payload?.eventsTriggered || 0) > 0 ||
            (latestEventId && latestEventId !== latestMarketplaceNotificationEventId);

          if (!cancelled && shouldRefreshCurrentUser && typeof onFetchCurrentUser === 'function') {
            onFetchCurrentUser({
              enforce: true,
              updateHasListings: false,
            }).catch(() => {});
          }
        })
        .catch(() => {});
    };

    runNotificationSync();
    const intervalId = window.setInterval(
      runNotificationSync,
      MARKETPLACE_NOTIFICATION_SYNC_INTERVAL
    );

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    mounted,
    isAuthenticated,
    currentUserId,
    latestMarketplaceNotificationEventId,
    onFetchCurrentUser,
  ]);

  const handleSubmit = values => {
    const { currentSearchParams, history, location, config, routeConfiguration } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );
  const showSellerVerificationLink = shouldShowSellerVerificationNavigation({
    isAuthenticated,
    isProvider,
    currentUser,
  });
  const sellerApprovalMatrix = getSellerApprovalMatrix({
    currentUser,
    isAuthenticated,
    isProvider,
  });
  const isSellerVerificationApproved = isSellerApproved(currentUser);
  const sellerVerificationStatus =
    currentUser?.attributes?.profile?.privateData?.sellerVerification?.status || '';
  const sellerVerificationNotificationEventId = getSellerVerificationNotificationEventId(
    currentUser
  );
  const latestOfferNotification = getLatestOfferNotification(currentUser);
  const latestOfferNotificationEventId = getLatestOfferNotificationEventId(currentUser);
  const {
    canBuy,
    canCreateListing,
    listingAccessPending,
    marketplaceApprovalPending,
  } = sellerApprovalMatrix;
  const createListingTarget = getCreateListingRedirectTarget(
    currentUser,
    DEFAULT_LISTING_CREATION_PATH
  );
  const approvedSellerVerificationRedirectTarget =
    getApprovedSellerVerificationRedirectTarget(currentUser);
  const showSellerVerificationBanner =
    mounted &&
    sellerVerificationNotificationEventId &&
    sellerVerificationNotificationEventId !== dismissedSellerVerificationEventId;
  const showOfferNotificationBanner =
    mounted &&
    latestOfferNotificationEventId &&
    latestOfferNotificationEventId !== dismissedOfferNotificationEventId;
  const showMarketplaceNotificationBanner =
    mounted &&
    latestMarketplaceNotificationEventId &&
    latestMarketplaceNotificationEventId !== dismissedMarketplaceNotificationEventId;

  const dismissSellerVerificationBanner = () => {
    if (!sellerVerificationNotificationEventId) {
      return;
    }

    writeSellerVerificationBannerEventId(sellerVerificationNotificationEventId);
    setDismissedSellerVerificationEventId(sellerVerificationNotificationEventId);
  };

  const dismissOfferNotificationBanner = () => {
    if (!latestOfferNotificationEventId) {
      return;
    }

    writeOfferNotificationBannerEventId(latestOfferNotificationEventId);
    setDismissedOfferNotificationEventId(latestOfferNotificationEventId);
  };

  const dismissMarketplaceNotificationBanner = () => {
    if (!latestMarketplaceNotificationEventId) {
      return;
    }

    writeMarketplaceNotificationBannerEventId(latestMarketplaceNotificationEventId);
    setDismissedMarketplaceNotificationEventId(latestMarketplaceNotificationEventId);
  };

  /**
   * Determine which tab to use in the inbox link:
   * - if only provider role – sales
   * - if only customer role – orders
   * - if both roles – determine by currentUserHasListings value
   */
  const topbarInboxTab = !isCustomer
    ? 'sales'
    : !isProvider
    ? 'orders'
    : currentUserHasListings
    ? 'sales'
    : 'orders';

  const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
      showCreateListingsLink={showCreateListingsLink}
      inboxTab={topbarInboxTab}
      viewport={viewport}
      showSellerVerificationLink={showSellerVerificationLink}
      showAdminVerificationLink={hasAdminVerificationAccess}
      isSellerVerificationApproved={isSellerVerificationApproved}
      createListingTarget={createListingTarget}
    />
  );

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  // Search form is shown conditionally depending on configuration and
  // the current page.
  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE &&
    ['SearchPage', 'SearchPageWithListingType'].includes(resolvedCurrentPage);
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

  const showSearchForm =
    showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

  const mobileSearchButtonMaybe = showSearchForm ? (
    <Button
      rootClassName={css.searchMenu}
      onClick={() => redirectToURLWithModalState(history, location, 'mobilesearch')}
      title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
    >
      <SearchIcon
        className={css.searchMenuIcon}
        ariaLabel={intl.formatMessage({ id: 'Topbar.searchIcon' })}
      />
    </Button>
  ) : (
    <div className={css.searchMenu} />
  );
  const SignupLink = !isAuthenticated ? (
    <NamedLink name="SignupPage" className={css.signupBtn}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  ) : null;
  const LoginLink = !isAuthenticated ? (
    <NamedLink name="LoginPage" className={css.loginBtn}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  ) : null;

  const customLinkItems = customLinks.map((linkConfig, index) => {
    const isInternal = linkConfig.type === 'internal' && linkConfig.route;
    const icon = isInternal ? FiLink : FiExternalLink;
    return {
      key: `custom_${index}`,
      label: linkConfig.text,
      icon,
      external: !isInternal,
      href: linkConfig.href,
      name: linkConfig.route?.name,
      params: linkConfig.route?.params,
      to: linkConfig.route?.to,
    };
  });
  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;
  const user = ensureCurrentUser(currentUser);
  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    return isTopbarMenuPageActive(resolvedCurrentPage, page) ? css.currentPage : null;
  };
  const manageListingsLinkMaybe = showCreateListingsLink
    ? {
        key: 'your-listings',
        label: <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />,
        icon: FiGrid,
        name: 'ManageListingsPage',
        currentPageKey: 'ManageListingsPage',
      }
    : null;

  const authorListingId = currentUser?.attributes?.profile?.publicData?.authorListingId || '';
  const title = currentUser?.attributes?.profile?.displayName;
  const slug = title && createSlug(title);
  const isChefProfilePublished = currentUser?.attributes?.profile?.publicData?.isPublished;

  const buildMenuItem = item => {
    const Icon = item.icon;
    const content = (
      <>
        <span className={css.menuItemContent}>
          {Icon ? <Icon className={css.menuIcon} aria-hidden /> : null}
          <span className={css.menuLabel}>{item.label}</span>
        </span>
        <span className={css.menuRight}>
          {item.rightAccessory ? item.rightAccessory : null}
          <FiChevronRight className={css.menuChevron} aria-hidden />
        </span>
      </>
    );

    const className = classNames(
      css.menuItem,
      item.currentPageKey ? currentPageClass(item.currentPageKey) : null
    );

    return item.external ? (
      <ExternalLink key={item.key} href={item.href} className={className}>
        {content}
      </ExternalLink>
    ) : (
      <NamedLink
        key={item.key}
        name={item.name}
        params={item.params}
        to={item.to}
        className={className}
      >
        {content}
      </NamedLink>
    );
  };

  const userLinks = [
    {
      key: 'auctions',
      label: <FormattedMessage id="TopbarDesktop.nav.auctions" />,
      icon: MdGavel,
      name: 'SearchPage',
      to: createSearchPageTo(AUCTIONS_SEARCH_PARAMS),
    },
    {
      key: 'buy-now',
      label: <FormattedMessage id="TopbarDesktop.nav.buyNow" />,
      icon: FiShoppingCart,
      name: 'SearchPage',
      to: createSearchPageTo(),
    },
    {
      key: 'start-selling',
      label: <FormattedMessage id="TopbarDesktop.nav.startSelling" />,
      icon: FiPlusCircle,
      name: createListingTarget.name,
      params: createListingTarget.params,
      to: createListingTarget.to,
    },
    {
      key: 'chat',
      label: 'Chat With Us',
      icon: FiMessageCircle,
      name: 'LandingPage',
    },
  ];

  const accountLinks = isAuthenticated
    ? [
        {
          key: 'my-orders',
          label: <FormattedMessage id="TopbarMobileMenu.inboxLink" />,
          icon: FiShoppingBag,
          name: topbarInboxTab === 'sales' ? 'SalesPage' : 'OrdersPage',
          currentPageKey: `TransactionsPage:${topbarInboxTab}`,
          rightAccessory: notificationCountBadge,
        },
        manageListingsLinkMaybe,
        {
          key: 'wishlist',
          label: <FormattedMessage id="TopbarMobileMenu.favoritesLink" />,
          icon: FiHeart,
          name: 'FavoritesPage',
          currentPageKey: 'FavoritesPage',
        },
        {
          key: 'profile-settings',
          label: <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />,
          icon: FiUser,
          name: 'ProfileSettingsPage',
          currentPageKey: 'ProfileSettingsPage',
        },
        {
          key: 'account-settings',
          label: <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />,
          icon: FiSettings,
          name: 'AccountSettingsPage',
          currentPageKey: 'AccountSettingsPage',
        },
        showSellerVerificationLink
          ? {
              key: 'seller-verification',
              label: 'Seller Verification',
              icon: FiUser,
              name: 'SellerVerificationPage',
              currentPageKey: 'SellerVerificationPage',
              rightAccessory: isSellerVerificationApproved ? (
                <span className={css.statusBadge}>Approved</span>
              ) : null,
            }
          : null,
        hasAdminVerificationAccess
          ? {
              key: 'admin-verification',
              label: 'Admin Verification',
              icon: FiSettings,
              name: 'AdminVerificationPage',
              currentPageKey: 'AdminVerificationPage',
            }
          : null,
      ].filter(Boolean)
    : [];

  const extraLinks = [
    {
      key: 'about',
      label: <FormattedMessage id="TopbarDesktop.nav.about" />,
      icon: FiInfo,
      name: 'AboutUsPage',
    },
    {
      key: 'contact',
      label: <FormattedMessage id="TopbarDesktop.nav.contactUs" />,
      icon: FiMessageCircle,
      name: 'ContactPage',
    },
    {
      key: 'finance',
      label: <FormattedMessage id="TopbarDesktop.nav.finance" />,
      icon: FiCreditCard,
      name: 'FinancePage',
    },
    ...customLinkItems,
  ];

  const sellerVerificationBanner =
    sellerVerificationStatus === 'changes_requested'
      ? {
          title: intl.formatMessage({
            id: 'Topbar.sellerVerification.changesRequestedTitle',
            defaultMessage: 'Additional information requested',
          }),
          message: intl.formatMessage({
            id: 'Topbar.sellerVerification.changesRequestedMessage',
            defaultMessage:
              'Chevalier Casks reviewed your seller verification and requested additional information. Review the requested fields and resubmit your application.',
          }),
          ctaLabel: intl.formatMessage({
            id: 'Topbar.sellerVerification.reviewApplication',
            defaultMessage: 'Review application',
          }),
          cta: {
            name: 'SellerVerificationPage',
          },
        }
      : sellerVerificationStatus === 'approved'
      ? {
          title: intl.formatMessage({
            id: 'Topbar.sellerVerification.approvedTitle',
            defaultMessage: 'Seller verification approved',
          }),
          message: marketplaceApprovalPending
            ? intl.formatMessage({
                id: 'Topbar.sellerVerification.accountApprovalPendingMessage',
                defaultMessage:
                  'Chevalier Casks approved your seller verification. Your marketplace account is still pending activation, so buying and listing are not available yet.',
              })
            : canCreateListing
            ? intl.formatMessage({
                id: 'Topbar.sellerVerification.approvedMessage',
                defaultMessage:
                  'Chevalier Casks approved your seller verification. Your marketplace account is active, and you can now post a new listing.',
              })
            : intl.formatMessage({
                id: 'Topbar.sellerVerification.approvedMessageSeparateAccess',
                defaultMessage:
                  'Chevalier Casks approved your seller verification. Listing access is still pending, so you cannot post a new listing yet.',
              }),
          ctaLabel: canCreateListing
            ? intl.formatMessage({
                id: 'Topbar.sellerVerification.startListing',
                defaultMessage: 'Post a listing',
              })
            : marketplaceApprovalPending
            ? intl.formatMessage({
                id: 'Topbar.sellerVerification.reviewAccountStatus',
                defaultMessage: 'Review status',
              })
            : intl.formatMessage({
                id: 'Topbar.sellerVerification.reviewApplication',
                defaultMessage: 'Review application',
              }),
          cta: canCreateListing
            ? {
                name: createListingTarget.name,
                params: createListingTarget.params,
                to: createListingTarget.to,
              }
            : approvedSellerVerificationRedirectTarget || {
                name: 'AccountSettingsPage',
              },
          secondaryMessage: marketplaceApprovalPending
            ? null
            : canBuy
            ? listingAccessPending
              ? intl.formatMessage({
                  id: 'Topbar.sellerVerification.purchaseAccessActive',
                  defaultMessage: 'Buying access is already active on your marketplace account.',
                })
              : null
            : intl.formatMessage({
                id: 'Topbar.sellerVerification.purchaseAccessPending',
                defaultMessage:
                  'Buying access is still pending separately from seller verification.',
              }),
        }
      : sellerVerificationStatus === 'rejected'
      ? {
          title: intl.formatMessage({
            id: 'Topbar.sellerVerification.rejectedTitle',
            defaultMessage: 'Seller verification update',
          }),
          message: intl.formatMessage({
            id: 'Topbar.sellerVerification.rejectedMessage',
            defaultMessage:
              'Your seller verification was not approved. Review the details on your Seller Verification page.',
          }),
          ctaLabel: intl.formatMessage({
            id: 'Topbar.sellerVerification.reviewApplication',
            defaultMessage: 'Review application',
          }),
          cta: {
            name: 'SellerVerificationPage',
          },
        }
      : null;

  const offerNotificationBanner = latestOfferNotification
    ? (() => {
        const listingTitle =
          latestOfferNotification.listingTitle ||
          intl.formatMessage({
            id: 'Topbar.offerNotification.defaultListingTitle',
            defaultMessage: 'this listing',
          });
        const routeName =
          latestOfferNotification.recipientRole === 'provider'
            ? 'SaleDetailsPage'
            : 'OrderDetailsPage';
        const cta = latestOfferNotification.transactionId
          ? {
              name: routeName,
              params: {
                id: latestOfferNotification.transactionId,
              },
            }
          : {
              name:
                latestOfferNotification.recipientRole === 'provider' ? 'SalesPage' : 'OrdersPage',
            };

        if (latestOfferNotification.type === 'offer_received') {
          return {
            title: intl.formatMessage(
              {
                id: 'Topbar.offerNotification.offerReceivedTitle',
                defaultMessage: 'You have an offer for {listingTitle}.',
              },
              { listingTitle }
            ),
            message: intl.formatMessage(
              {
                id: 'Topbar.offerNotification.offerReceivedMessage',
                defaultMessage: 'The latest offer price and terms are available in sale details.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.offerNotification.reviewOffer',
              defaultMessage: 'Review offer',
            }),
            cta,
          };
        }

        if (latestOfferNotification.type === 'counter_offer_received') {
          return {
            title: intl.formatMessage(
              {
                id: 'Topbar.offerNotification.counterOfferReceivedTitle',
                defaultMessage: 'Counter offer for {listingTitle}',
              },
              { listingTitle }
            ),
            message: intl.formatMessage(
              {
                id: 'Topbar.offerNotification.counterOfferReceivedMessage',
                defaultMessage:
                  'A counter offer is available for {listingTitle}. Review the transaction details for the latest price and terms.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.offerNotification.reviewOffer',
              defaultMessage: 'Review offer',
            }),
            cta,
          };
        }

        if (latestOfferNotification.type === 'offer_declined') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.offerNotification.offerDeclinedTitle',
              defaultMessage: 'Offer declined',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.offerNotification.offerDeclinedMessage',
                defaultMessage: 'The latest offer for {listingTitle} was declined.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.offerNotification.viewTransaction',
              defaultMessage: 'View transaction',
            }),
            cta,
          };
        }

        if (latestOfferNotification.type === 'offer_accepted') {
          return {
            title: intl.formatMessage(
              {
                id: 'Topbar.offerNotification.offerAcceptedTitle',
                defaultMessage: 'Your offer for {listingTitle} was accepted',
              },
              { listingTitle }
            ),
            message: intl.formatMessage(
              {
                id: 'Topbar.offerNotification.offerAcceptedMessage',
                defaultMessage:
                  'Your latest offer for {listingTitle} has been accepted. Complete checkout from order details to confirm the order.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.offerNotification.viewTransaction',
              defaultMessage: 'View transaction',
            }),
            cta,
          };
        }

        return null;
      })()
    : null;

  const marketplaceNotificationBanner = latestMarketplaceNotification
    ? (() => {
        const listingTitle =
          latestMarketplaceNotification.listingTitle ||
          intl.formatMessage({
            id: 'Topbar.marketplaceNotification.defaultListingTitle',
            defaultMessage: 'this listing',
          });
        const transactionRouteName =
          latestMarketplaceNotification.recipientRole === 'provider'
            ? 'SaleDetailsPage'
            : 'OrderDetailsPage';
        const transactionCta = latestMarketplaceNotification.transactionId
          ? {
              name: transactionRouteName,
              params: {
                id: latestMarketplaceNotification.transactionId,
              },
            }
          : {
              name:
                latestMarketplaceNotification.recipientRole === 'provider'
                  ? 'SalesPage'
                  : 'OrdersPage',
            };
        const manageListingsCta = {
          name: 'ManageListingsPage',
        };

        if (latestMarketplaceNotification.type === 'listing_submitted') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.listingSubmittedTitle',
              defaultMessage: 'Listing submitted for review',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.marketplaceNotification.listingSubmittedMessage',
                defaultMessage: '"{listingTitle}" is pending review and is not live yet.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.manageListings',
              defaultMessage: 'Manage listings',
            }),
            cta: manageListingsCta,
          };
        }

        if (latestMarketplaceNotification.type === 'listing_approved') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.listingApprovedTitle',
              defaultMessage: 'Listing approved',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.marketplaceNotification.listingApprovedMessage',
                defaultMessage: '"{listingTitle}" is now live on the marketplace.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.manageListings',
              defaultMessage: 'Manage listings',
            }),
            cta: manageListingsCta,
          };
        }

        if (latestMarketplaceNotification.type === 'listing_rejected') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.listingRejectedTitle',
              defaultMessage: 'Listing review update',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.marketplaceNotification.listingRejectedMessage',
                defaultMessage:
                  '"{listingTitle}" is not live yet. Review the latest status in Manage Listings.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.manageListings',
              defaultMessage: 'Manage listings',
            }),
            cta: manageListingsCta,
          };
        }

        if (latestMarketplaceNotification.type === 'bsa_sent') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.bsaSentTitle',
              defaultMessage: 'Bulk Sales Agreement ready',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.marketplaceNotification.bsaSentMessage',
                defaultMessage:
                  'The Bulk Sales Agreement for "{listingTitle}" is ready in DocuSign.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.viewTransaction',
              defaultMessage: 'View transaction',
            }),
            cta: transactionCta,
          };
        }

        if (latestMarketplaceNotification.type === 'bsa_signature_needed') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.bsaSignatureNeededTitle',
              defaultMessage: 'Action needed on BSA',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.marketplaceNotification.bsaSignatureNeededMessage',
                defaultMessage:
                  'The other party has already signed the Bulk Sales Agreement for "{listingTitle}". Your signature is still required.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.reviewBsa',
              defaultMessage: 'Review agreement',
            }),
            cta: transactionCta,
          };
        }

        if (latestMarketplaceNotification.type === 'bsa_fully_executed') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.bsaFullyExecutedTitle',
              defaultMessage: 'Bulk Sales Agreement executed',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.marketplaceNotification.bsaFullyExecutedMessage',
                defaultMessage:
                  'The Bulk Sales Agreement for "{listingTitle}" is fully executed and all required signatures are complete.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.viewTransaction',
              defaultMessage: 'View transaction',
            }),
            cta: transactionCta,
          };
        }

        if (latestMarketplaceNotification.type === 'initial_deposit_paid') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.initialDepositPaidTitle',
              defaultMessage: 'Initial deposit paid',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.marketplaceNotification.initialDepositPaidMessage',
                defaultMessage: 'Initial deposit for "{listingTitle}" has been received.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.viewTransaction',
              defaultMessage: 'View transaction',
            }),
            cta: transactionCta,
          };
        }

        if (latestMarketplaceNotification.type === 'final_payment_requested') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.finalPaymentRequestedTitle',
              defaultMessage: 'Final payment due',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.marketplaceNotification.finalPaymentRequestedMessage',
                defaultMessage: 'The remaining balance for "{listingTitle}" is now due.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.reviewPayment',
              defaultMessage: 'Review payment',
            }),
            cta: transactionCta,
          };
        }

        if (latestMarketplaceNotification.type === 'final_payment_paid') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.finalPaymentPaidTitle',
              defaultMessage: 'Final payment paid',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.marketplaceNotification.finalPaymentPaidMessage',
                defaultMessage: 'Final payment for "{listingTitle}" has been received.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.viewTransaction',
              defaultMessage: 'View transaction',
            }),
            cta: transactionCta,
          };
        }

        if (latestMarketplaceNotification.type === 'order_in_process') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.orderInProcessTitle',
              defaultMessage: 'Order in process',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.marketplaceNotification.orderInProcessMessage',
                defaultMessage:
                  '"{listingTitle}" is now in process. Review the transaction for the next required steps.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.viewTransaction',
              defaultMessage: 'View transaction',
            }),
            cta: transactionCta,
          };
        }

        if (latestMarketplaceNotification.type === 'order_delivered') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.orderDeliveredTitle',
              defaultMessage: 'Order delivered',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.marketplaceNotification.orderDeliveredMessage',
                defaultMessage:
                  '"{listingTitle}" was marked as delivered. Review the transaction when you are ready to confirm receipt.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.viewTransaction',
              defaultMessage: 'View transaction',
            }),
            cta: transactionCta,
          };
        }

        if (latestMarketplaceNotification.type === 'order_received') {
          return {
            title: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.orderReceivedTitle',
              defaultMessage: 'Order received',
            }),
            message: intl.formatMessage(
              {
                id: 'Topbar.marketplaceNotification.orderReceivedMessage',
                defaultMessage:
                  '"{listingTitle}" was marked as received and the transaction is moving toward completion.',
              },
              { listingTitle }
            ),
            ctaLabel: intl.formatMessage({
              id: 'Topbar.marketplaceNotification.viewTransaction',
              defaultMessage: 'View transaction',
            }),
            cta: transactionCta,
          };
        }

        return null;
      })()
    : null;

  return (
    <div className={classes}>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      {showSellerVerificationBanner && sellerVerificationBanner ? (
        <div className={css.sellerVerificationBanner} role="status">
          <div className={css.sellerVerificationBannerContent}>
            <div className={css.sellerVerificationBannerText}>
              <div className={css.sellerVerificationBannerTitle}>
                {sellerVerificationBanner.title}
              </div>
              <div>{sellerVerificationBanner.message}</div>
              {sellerVerificationBanner.secondaryMessage ? (
                <div className={css.sellerVerificationBannerSecondary}>
                  {sellerVerificationBanner.secondaryMessage}
                </div>
              ) : null}
            </div>
            <div className={css.sellerVerificationBannerActions}>
              <NamedLink
                name={sellerVerificationBanner.cta.name}
                params={sellerVerificationBanner.cta.params}
                to={sellerVerificationBanner.cta.to}
                className={css.sellerVerificationBannerLink}
              >
                {sellerVerificationBanner.ctaLabel}
              </NamedLink>
              <PrimaryButton
                type="button"
                className={css.sellerVerificationBannerDismissPrimary}
                onClick={dismissSellerVerificationBanner}
              >
                <FormattedMessage id="Topbar.sellerVerification.dismiss" defaultMessage="Dismiss" />
              </PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}
      {showOfferNotificationBanner && offerNotificationBanner ? (
        <div className={css.sellerVerificationBanner} role="status">
          <div className={css.sellerVerificationBannerContent}>
            <div className={css.sellerVerificationBannerText}>
              <div className={css.sellerVerificationBannerTitle}>
                {offerNotificationBanner.title}
              </div>
              <div>{offerNotificationBanner.message}</div>
            </div>
            <div className={css.sellerVerificationBannerActions}>
              <NamedLink
                name={offerNotificationBanner.cta.name}
                params={offerNotificationBanner.cta.params}
                to={offerNotificationBanner.cta.to}
                className={css.sellerVerificationBannerLink}
              >
                {offerNotificationBanner.ctaLabel}
              </NamedLink>
              <InlineTextButton
                rootClassName={css.sellerVerificationBannerDismiss}
                onClick={dismissOfferNotificationBanner}
              >
                <FormattedMessage id="Topbar.offerNotification.dismiss" defaultMessage="Dismiss" />
              </InlineTextButton>
            </div>
          </div>
        </div>
      ) : null}
      {showMarketplaceNotificationBanner && marketplaceNotificationBanner ? (
        <div className={css.sellerVerificationBanner} role="status">
          <div className={css.sellerVerificationBannerContent}>
            <div className={css.sellerVerificationBannerText}>
              <div className={css.sellerVerificationBannerTitle}>
                {marketplaceNotificationBanner.title}
              </div>
              <div>{marketplaceNotificationBanner.message}</div>
            </div>
            <div className={css.sellerVerificationBannerActions}>
              <NamedLink
                name={marketplaceNotificationBanner.cta.name}
                params={marketplaceNotificationBanner.cta.params}
                to={marketplaceNotificationBanner.cta.to}
                className={css.sellerVerificationBannerLink}
              >
                {marketplaceNotificationBanner.ctaLabel}
              </NamedLink>
              <PrimaryButton
                type="button"
                className={css.sellerVerificationBannerDismissPrimary}
                onClick={dismissMarketplaceNotificationBanner}
              >
                <FormattedMessage
                  id="Topbar.marketplaceNotification.dismiss"
                  defaultMessage="Dismiss"
                />
              </PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}
      <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        <div className={css.mobileTopBar}>
          <LinkedLogo
            layout={'mobile'}
            alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
            linkToExternalSite={config?.topbar?.logoLink}
          />
          <div className={css.rightLinks}>
            <Button
              rootClassName={css.menu}
              onClick={() => setIsOpen(!isOpen)}
              title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
            >
              <MenuIcon
                className={css.menuIcon}
                ariaLabel={intl.formatMessage({ id: 'Topbar.menuIcon' })}
              />
              {notificationDot}
            </Button>
            {/* <CustomLinksMenu
              currentPage={currentPage}
              customLinks={customLinks}
              intl={intl}
              hasClientSideContentReady={
                authenticatedOnClientSide || !isAuthenticatedOrJustHydrated
              }
              showCreateListingsLink={showCreateListingsLink}
            />
            {LoginLink}
            {isAuthenticated ? (
              <AvatarSmall className={css.avatar} user={currentUser} disableProfileLink />
            ) : null} */}
          </div>
        </div>
      </div>
      <div className={css.desktop}>
        <TopbarDesktop
          viewport={viewport}
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          onSearchSubmit={handleSubmit}
          config={config}
          customLinks={customLinks}
          showSearchForm={showSearchForm}
          showCreateListingsLink={showCreateListingsLink}
          inboxTab={topbarInboxTab}
          showSellerVerificationLink={showSellerVerificationLink}
          showAdminVerificationLink={hasAdminVerificationAccess}
          isSellerVerificationApproved={isSellerVerificationApproved}
          createListingTarget={createListingTarget}
        />
      </div>
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.topbarDrawer}
        isOpen={isOpen}
        onClose={() => setIsOpen(!isOpen)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        mobileMenuModal={true}
      >
        <div className={css.drawerBody}>
          <div className={css.content}>
            {isAuthenticated ? (
              <div className={css.greetingSec}>
                <h3>
                  <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
                </h3>
                <InlineTextButton rootClassName={css.logoutButton} onClick={handleLogout}>
                  <FormattedMessage id="TopbarMobileMenu.logoutLink" />
                </InlineTextButton>
              </div>
            ) : null}
            <div className={classNames(css.menuSection, css.menuSectionBrowse)}>
              <div className={css.sectionTitle}>Browse</div>
              <div className={css.menuList}>{userLinks.map(buildMenuItem)}</div>
            </div>

            {isAuthenticated ? (
              <div className={classNames(css.menuSection, css.menuSectionAccount)}>
                <div className={css.sectionTitle}>Account</div>
                <div className={css.menuList}>{accountLinks.map(buildMenuItem)}</div>
              </div>
            ) : null}

            <div className={classNames(css.menuSection, css.menuSectionMore)}>
              <div className={css.sectionTitle}>More</div>
              <div className={css.menuList}>{extraLinks.map(buildMenuItem)}</div>
            </div>

            <div className={css.spacer} />
          </div>

          {!isAuthenticated ? (
            <div className={css.joinUsSec}>
              {LoginLink}
              {SignupLink}
            </div>
          ) : null}
        </div>
      </Modal>
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.modalContainer}
        isOpen={isMobileMenuOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainerSearchForm}
        isOpen={isMobileSearchOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilesearch')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.searchContainer}>
          <TopbarSearchForm
            onSubmit={handleSubmit}
            initialValues={initialSearchFormValues}
            isMobile
            appConfig={config}
          />
          <p className={css.mobileHelp}>
            <FormattedMessage id="Topbar.mobileSearchHelp" />
          </p>
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />
      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
